import { Helmet } from "react-helmet";
import React, { useState, useEffect } from "react";
import { Img, Heading, Button } from "../../components";
import { ReactTable } from "../../components/ReactTable";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import { createColumnHelper } from "@tanstack/react-table";
import "styles/dashboard.css";
import { useNavigate } from "react-router-dom";
import CheckBox from "components/CheckBox";
import axios from "axios";
import { useAppContext } from "components/Context/AppContext";

function createColumn(accessor, label) {
  const tableColumnHelper = createColumnHelper();
  return tableColumnHelper.accessor(accessor, {
    cell: (info) => (
      <Heading as="p" size="textxs" className="text-white-a700">
        {info.getValue()}
      </Heading>
    ),
    header: () => (
      <Heading
        size="headingxs"
        as="h6"
        className="pb-[22px] text-left text-[12px] font-bold text-white-a700 sm:pb-5"
      >
        {label}
      </Heading>
    ),
    meta: { width: "104px" },
  });
}

const apiUrl = process.env.REACT_APP_API_URL;

export default function UploadPage() {
  const [data, setData] = useState([]);
  const [token, setToken] = useState(null);
  const [refreshDB, setRefreshDB] = useState(false);
  const { isProcessRunning } = useAppContext();
  const navigate = useNavigate();

  const handleClickNew = () => {
    navigate("/portfolioUpload");
  };

  const handleDelete = async () => {
    const PORT_ID_LIST = data
      ? data.filter((row) => row.isChecked).map((row) => row.port_id)
      : [];
    try {
      const response = await axios.post(
        `${apiUrl}/delete_portfolios`,
        { PORT_ID_LIST },
        {
          params: { token },
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      console.log(response.data.message);
      setRefreshDB(!refreshDB);
    } catch (error) {
      console.error("Error handleDelete portfolio data:", error);
    }
  };

  useEffect(() => {
    const tok = sessionStorage.getItem("token");
    setToken(tok);
    const fetchData = async () => {
      try {
        const response = await axios.post(
          `${apiUrl}/get_dashboard`,
          {},
          {
            params: { token: tok },
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        const fetchedData = response.data.TableData.map((row) => ({
          ...row,
          isChecked: false,
        }));
        setData(fetchedData);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };
    fetchData();

    let intervalId;
    if (isProcessRunning) {
      intervalId = setInterval(fetchData, 3000);
    }
    return () => clearInterval(intervalId);
  }, [refreshDB, isProcessRunning]);

  const handleCheckboxChange = (rowIndex, isChecked) => {
    const updatedData = data.map((row, index) =>
      index === rowIndex ? { ...row, isChecked } : row
    );
    setData(updatedData);
  };

  const tableColumns = React.useMemo(() => {
    const tableColumnHelper = createColumnHelper();
    return [
      tableColumnHelper.accessor("portfolio_name", {
        cell: (info) => (
          <div className="flex flex-1 items-end justify-center gap-3.5">
            <div className="mb-3 flex flex-1 items-center gap-[9px]">
              <CheckBox
                checked={info.row.original.isChecked}
                onChange={(isChecked) =>
                  handleCheckboxChange(info.row.index, isChecked)
                }
                name="Portfolio Name Checkbox"
                label={info.getValue()}
                id="PortfolioNameCheckbox"
                variant="secondary"
                className="gap-2 self-start text-[11px] font-medium text-white-a700"
              />
            </div>
          </div>
        ),
        header: () => (
          <Heading
            size="headingxs"
            as="h2"
            className="pb-[22px] pl-[34px] text-left text-[12px] font-bold text-white-a700 sm:pb-5 sm:pl-5"
          >
            Portfolio Name
          </Heading>
        ),
        meta: { width: "168px" },
      }),
      createColumn("as_of_date", "As of Date"),
      createColumn("market_value", "Market Value"),
      createColumn("tail_measure", "Tail Measure"),
      createColumn("benchmark", "Benchmark"),
      createColumn("create_date", "Created Date"),
      createColumn("created_by", "Created By"),
      tableColumnHelper.accessor("status", {
        cell: (info) => {
          const status = info.getValue();
          const url = info.row.original.report_url;
          const message = info.row.original.message;
          return status === "success" ? (
            <a href={url} className="text-green-500 underline" target="_blank" rel="noopener noreferrer">
              Report
            </a>
          ) : status === "partial" ? (
            <a href={url} className="text-yellow-500 underline" target="_blank" rel="noopener noreferrer">
              Partial Report
            </a>
          ) : status === "error" ? (
            <a href={apiUrl + "/download/" + message + "?token=" + token} className="text-red-500 underline">
              Error
            </a>
          ) : (
            <Heading as="p" size="textxs" className="text-white-a700">
              {status}
            </Heading>
          );
        },
        header: () => (
          <Heading
            size="headingxs"
            as="h6"
            className="pb-[22px] text-left text-[12px] font-bold text-white-a700 sm:pb-5"
          >
            Status
          </Heading>
        ),
        meta: { width: "104px" },
      }),
      tableColumnHelper.accessor("filename", {
        cell: (info) => {
          const filename = info.getValue();
          const group_id = info.row.original.group_id;
          const url = apiUrl + "/download/" + group_id + "/" + filename + "?token=" + token;
          return (
            <a href={url} className="text-white-a700 underline">
              download
            </a>
          );
        },
        header: () => (
          <Heading
            size="headingxs"
            as="h6"
            className="pb-[22px] text-left text-[12px] font-bold text-white-a700 sm:pb-5"
          >
            Download
          </Heading>
        ),
        meta: { width: "104px" },
      }),
    ];
  });

  return (
    <>
      <Helmet>
        <title>Upload – Tail Risk Global</title>
        <meta name="description" content="Upload and manage your portfolios." />
      </Helmet>

      <div className="trg-shell">

        <DashboardTopbar />

        <DashboardMenuBar activePage="Portfolio Upload" />

        {/* Main content */}
        <main style={{ flex: 1, padding: "36px 40px", overflowY: "auto", overflowX: "hidden", background: "#181719", minHeight: 0 }}>

            {/* Page header */}
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 28 }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--gray_500)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 4 }}>
                  Portfolio Manager
                </div>
                <div style={{ fontSize: 26, fontWeight: 600, color: "var(--white_a700)", letterSpacing: "0.3px" }}>
                  Upload
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex justify-between mb-6">
              <Button onClick={handleClickNew} color="green_A700" size="xs1" shape="square" className="min-w-[90px] px-5">
                Upload Portfolio
              </Button>
              <Button onClick={handleDelete} color="red_500" size="xs1" shape="square" className="min-w-[146px] px-3">
                Delete Selected
              </Button>
            </div>

            {/* Table */}
            <div
              style={{
                background: "var(--gray_900_01)",
                border: "1px solid var(--gray_900)",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <ReactTable
                size="xs"
                bodyProps={{}}
                headerProps={{ className: "border-white-a700 border-b border-solid" }}
                cellProps={{
                  style: { fontSize: "10px", color: "#ffffff" },
                  className: "border-white-a700 border-b border-solid text-xs",
                }}
                className="w-full md:block md:overflow-x-auto md:whitespace-nowrap"
                columns={tableColumns}
                data={data}
              />
            </div>

        </main>
      </div>
    </>
  );
}
